import { AbsoluteFill, useVideoConfig, Img } from 'remotion';

interface LogoPosition {
  x: 'left' | 'center' | 'right';
  y: 'top' | 'center' | 'bottom';
}

interface LogoProps {
  url?: string;
  position?: string;
  size?: number; // Percentage of smallest video dimension
  opacity?: number; // Percentage (0-100)
  horizontalMargin?: number; // Percentage of video width
  verticalMargin?: number; // Percentage of video height
}

const parsePosition = (position: string): LogoPosition => {
  // Handle special case of just "center"
  if (position === 'center') {
    return { x: 'center', y: 'center' };
  }

  const parts = position.split('-');
  let y: LogoPosition['y'] = 'bottom';
  let x: LogoPosition['x'] = 'right';

  // Determine vertical position (first part or standalone)
  if (parts[0] === 'top') {
    y = 'top';
  } else if (parts[0] === 'center') {
    y = 'center';
  } else if (parts[0] === 'bottom') {
    y = 'bottom';
  }

  // Determine horizontal position (second part or look for specific horizontal indicators)
  if (parts.length > 1) {
    if (parts[1] === 'left') {
      x = 'left';
    } else if (parts[1] === 'center') {
      x = 'center';
    } else if (parts[1] === 'right') {
      x = 'right';
    }
  } else {
    // For single-word positions like "center", it was already handled above
    // For compound positions that might have been split incorrectly,
    // check if any remaining parts indicate horizontal positioning
    if (parts.includes('left')) x = 'left';
    else if (parts.includes('right')) x = 'right';
  }

  return { x, y };
};

const Logo: React.FC<LogoProps> = ({
  url,
  position = 'bottom-right',
  size = 20,
  opacity = 70,
  horizontalMargin = 5,
  verticalMargin = 5
}) => {
  const { width, height } = useVideoConfig();

  if (!url) {
    return null;
  }

  const { x, y } = parsePosition(position);

  // Calculate logo size based on smallest dimension
  const smallestDimension = Math.min(width, height);
  const logoSize = (size / 100) * smallestDimension;

  // Calculate position
  const getPositionStyle = () => {
    const horizontalMarginPx = (horizontalMargin / 100) * width; // Convert percentage to pixels
    const verticalMarginPx = (verticalMargin / 100) * height; // Convert percentage to pixels
    const style: React.CSSProperties = {
      position: 'absolute',
      width: logoSize,
      height: 'auto',
      maxWidth: logoSize,
      maxHeight: logoSize,
      opacity: opacity / 100,
      objectFit: 'contain',
    };

    // Horizontal positioning
    switch (x) {
      case 'left':
        style.left = horizontalMarginPx;
        break;
      case 'center':
        style.left = '50%';
        style.transform = 'translateX(-50%)';
        break;
      case 'right':
        style.right = horizontalMarginPx;
        break;
    }

    // Vertical positioning
    switch (y) {
      case 'top':
        style.top = verticalMarginPx;
        break;
      case 'center':
        style.top = '50%';
        style.transform = style.transform
          ? `${style.transform} translateY(-50%)`
          : 'translateY(-50%)';
        break;
      case 'bottom':
        style.bottom = verticalMarginPx;
        break;
    }

    return style;
  };

  return (
    <AbsoluteFill>
      <Img
        src={url}
        style={getPositionStyle()}
      />
    </AbsoluteFill>
  );
};

export default Logo;
